import type { CourseProgress } from "@brookbush/database";
import type { CoursePreview, CourseSection } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import useSWR, { mutate as globalMutate } from "swr";

import fetcher from "~/utils/fetcher.client";

const useCourseProgress = (course?: CoursePreview) => {
	const { isSignedIn, userId } = useAuth();

	const {
		mutate,
		data: progress,
		...rest
	} = useSWR<CourseProgress[]>(
		isSignedIn && course && `/courses/${course.slug}/progress`,
	);

	const addCourseProgress = async (section: CourseSection) => {
		const keysToMark = [section._key];

		if (section.subSections?.length) {
			keysToMark.push(
				...section.subSections!.map((subSection) => subSection._key),
			);
		}

		const areSiblingSubSectionsMarked = !!section.parent
			?.subSections!.filter((subSection) => subSection._key !== section._key)
			.every((subSection) =>
				progress?.find(
					(progressItem) => progressItem.sectionKey === subSection._key,
				),
			);

		if (areSiblingSubSectionsMarked) {
			keysToMark.push(section.parent!._key);
		}

		await mutate(
			fetcher(`/courses/${course?.slug}/progress`, {
				method: "POST",
				data: {
					keys: keysToMark,
				},
			}),

			{
				optimisticData: progress?.concat(
					keysToMark.map((key, index) => ({
						courseId: course?._id!,
						userId: userId!,
						createdAt: new Date(),
						updatedAt: new Date(),
						sectionKey: key,
						id: progress.length
							? progress[progress.length - 1].id + 1 + index
							: 1 + index,
					})),
				),

				populateCache: false,
			},
		);

		globalMutate("/onboarding/checklist");
	};

	const removeCourseProgress = async (section: CourseSection) => {
		const keysToUnmark = [section._key];

		if (section.subSections?.length) {
			keysToUnmark.push(
				...section.subSections!.map((subSection) => subSection._key),
			);
		}

		if (section.parent) {
			keysToUnmark.push(section.parent._key);
		}

		mutate(
			fetcher(`/courses/${course?.slug}/progress`, {
				method: "DELETE",
				data: {
					keys: keysToUnmark,
				},
			}),

			{
				optimisticData: progress?.filter(
					(progressItem) =>
						keysToUnmark.indexOf(progressItem.sectionKey) === -1,
				),
				populateCache: false,
			},
		);
	};

	return {
		addCourseProgress,
		removeCourseProgress,
		progress,
		isLoadingCourseProgress: rest.isLoading,
		...rest,
	};
};

export default useCourseProgress;
